import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Fab,
  Container,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { ThemeProvider, useThemeMode } from './context/ThemeContext';
import { BudgetProvider } from './context/BudgetContext';
import MonthlySummary from './components/MonthlySummary';
import Filters from './components/Filters';
import EntryList from './components/EntryList';
import AddEntryForm from './components/AddEntryForm';
import CreditCardsTab from './components/CreditCardsTab';

// Import sample data utility for development
import './utils/sampleData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const AppContent: React.FC = () => {
  const { mode, toggleTheme } = useThemeMode();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
      {/* App Bar */}
      <AppBar position="sticky" elevation={0} sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}>
        <Toolbar sx={{ py: 0.5 }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}>
            <WalletIcon />
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 0.5 }}>
            Budget Tracker
          </Typography>
          <IconButton 
            color="inherit" 
            onClick={toggleTheme} 
            edge="end"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
              }
            }}
          >
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Toolbar>
        
        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{
            style: {
              backgroundColor: 'white',
              height: 3,
              borderRadius: '3px 3px 0 0',
            }
          }}
          sx={{ 
            bgcolor: 'rgba(0,0,0,0.1)',
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              py: 1.5,
            }
          }}
        >
          <Tab label="Budget" />
          <Tab label="Credit Cards" />
        </Tabs>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {/* Budget Tab */}
        <TabPanel value={currentTab} index={0}>
          <MonthlySummary />
          <Filters />
          <EntryList />
          
          {/* Floating Action Button for Budget */}
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setAddDialogOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 12px 32px rgba(102, 126, 234, 0.6)',
                transform: 'scale(1.1) rotate(90deg)',
              },
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                },
                '50%': {
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.6)',
                },
                '100%': {
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                },
              },
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            <AddIcon />
          </Fab>
        </TabPanel>

        {/* Credit Cards Tab */}
        <TabPanel value={currentTab} index={1}>
          <CreditCardsTab />
        </TabPanel>
      </Container>

      {/* Add Entry Dialog */}
      <AddEntryForm open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BudgetProvider>
        <AppContent />
      </BudgetProvider>
    </ThemeProvider>
  );
}

export default App;
